import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { FieldError } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import { getClassByVariant } from "helpers/getClassByVariant";
import { extractAddressDetails } from "helpers/googleMap";
import { showErrorMessage } from "helpers/toastMessage";
import { AddressInfo } from "models/User";

import styles from "./style.module.scss";
import "./_style.scss";

const GOOGLE_PLACES_LOAD_TIMEOUT_MS = 10000;
const GOOGLE_PLACES_CHECK_INTERVAL_MS = 100;

interface IProps {
  value?: AddressInfo | null;
  onChange?: (value: AddressInfo | null) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: FieldError;
  label?: string | React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  color?: "primary" | "secondary";
  isHideError?: boolean;
  containerClass?: string;
  inputClass?: string;
}

const GoogleAutoComplete: React.FC<IProps> = ({
  label,
  placeholder = "",
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  color = "primary",
  isHideError = false,
  containerClass,
  inputClass
}) => {
  const uniqueId = useRef(uuidv4()).current;
  const [inputValue, setInputValue] = useState(value?.formattedAddress || "");
  const autocompleteRef = useRef<HTMLInputElement>(null);
  const autocompleteInstanceRef = useRef<google.maps.places.Autocomplete | null>(null);
  const isSelectingPlace = useRef(false);
  const [apiLoaded, setApiLoaded] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    const isGooglePlacesAvailable = () => !!window.google?.maps?.places;

    if (isGooglePlacesAvailable()) {
      setApiLoaded(true);
      setApiError(null);
      return;
    }

    setApiLoaded(false);
    setApiError(null);

    const intervalId = window.setInterval(() => {
      if (!isGooglePlacesAvailable()) return;

      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
      setApiLoaded(true);
      setApiError(null);
    }, GOOGLE_PLACES_CHECK_INTERVAL_MS);

    const timeoutId = window.setTimeout(() => {
      if (!isGooglePlacesAvailable()) {
        window.clearInterval(intervalId);
        setApiError("Google Maps API not loaded properly");
      }
    }, GOOGLE_PLACES_LOAD_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, []);

  // Handle value changes separately from initialization
  useEffect(() => {
    // Update the input value when the value prop changes
    const newValue = [
      value?.address || value?.street_one,
      value?.city,
      value?.state,
      value?.zip,
      value?.country
    ]
      .filter((item) => !!item && item !== undefined)
      .join(", ");

    // Update state
    setInputValue(newValue);

    // Also update the DOM element directly to ensure consistency
    if (autocompleteRef.current) {
      autocompleteRef.current.value = newValue;
    }
  }, [value?.address, value?.city, value?.state, value?.zip, value?.country]);

  useEffect(() => {
    if (!apiLoaded || !autocompleteRef.current) return;

    try {
      const autocomplete = new google.maps.places.Autocomplete(autocompleteRef.current, {
        types: ["geocode"],
        componentRestrictions: { country: ["us", "ca", "mx"] },
        fields: ["address_components", "geometry", "formatted_address"]
      });

      autocompleteInstanceRef.current = autocomplete;

      autocomplete.addListener("place_changed", () => {
        if (!autocompleteInstanceRef.current) return;

        const place = autocompleteInstanceRef.current.getPlace();
        if (!place || !place.geometry?.location) {
          showErrorMessage("Please select a valid address");
          return;
        }

        const extracted = extractAddressDetails(place);
        isSelectingPlace.current = true;
        setInputValue(extracted.formattedAddress);
        onChange?.(extracted);
      });

      const pacObserver = new MutationObserver(() => {
        document.querySelectorAll(".pac-item:not([role])").forEach((item) => {
          item.setAttribute("role", "button");
        });
      });
      pacObserver.observe(document.body, { childList: true, subtree: true });

      return () => {
        google.maps.event.clearInstanceListeners(autocomplete);
        autocompleteInstanceRef.current = null;
        pacObserver.disconnect();
      };
    } catch (error) {
      setApiError("Could not initialize Google Maps autocomplete");
    }
  }, [apiLoaded, onChange]);

  // Handle input changes
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);

      // If user is typing, they're not selecting from dropdown
      if (!isSelectingPlace.current && value.trim() === "") {
        onChange?.(null);
      }
      isSelectingPlace.current = false;
    },
    [onChange]
  );

  // Handle blur event
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
    },
    [onBlur]
  );

  // Get appropriate CSS classes based on color variant
  const inputClasses = clsx(
    inputClass,
    styles.googleAutocompleteInput,
    getClassByVariant(color)?.input,
    !!error && "!border-b-error",
    "disabled:bg-inputDark",
    "disabled:cursor-not-allowed"
  );

  return (
    <div className={clsx(styles.googleAutocompleteContainer, containerClass)}>
      {label && (
        <label
          htmlFor={`address-input-${uniqueId}`}
          className={clsx(styles.googleAutocompleteLabel, getClassByVariant(color)?.label, {
            "cursor-not-allowed": disabled
          })}>
          {label}
        </label>
      )}

      <div className={styles.googleAutocompleteInputWrapper}>
        <input
          ref={autocompleteRef}
          id={`address-input-${uniqueId}`}
          type="text"
          className={inputClasses}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={typeof label === "string" ? label : "Address input"}
          aria-invalid={!!error || !!apiError}
          aria-describedby={
            apiError || (!isHideError && error) ? `address-error-${uniqueId}` : undefined
          }
          autoComplete="off"
        />
      </div>

      {apiError ? (
        <div className={styles.googleAutocompleteError} id={`address-error-${uniqueId}`}>
          {apiError}. Please check your Google Maps API configuration.
        </div>
      ) : null}

      {!apiError && !isHideError && error && (
        <div className={styles.googleAutocompleteError} id={`address-error-${uniqueId}`}>
          {error.message}
        </div>
      )}
    </div>
  );
};

export default memo(GoogleAutoComplete);
